import { ROUTE_ACCESS_MATRIX } from './routeAccessMatrix'
import { departmentToCode, normalizeRoleName } from './departmentCodes'
import { isHrLaborOnlyScoped, isProdSupervisorTeamScoped } from '../roleChecks'
import { isHrLaborPathAllowed } from './hrLaborRouteAllowlist'
import { isProdSupervisorPathAllowed } from './prodSupervisorRouteAllowlist'
import {
  ROUTE_ACL_DEBUG,
  LOG_PREFIX,
  logAcl,
  buildRoleKeyDebugInfo,
} from './routeAccessDebug'

/** Rutas que no pasan por ACL (login, móvil). */
export const PUBLIC_PATHS = new Set([
  '/login',
  '/mobile/scan',
  '/pda/sala-mezcla',
])

const FACTORY_PATH_CODES = new Set([
  'CXB', 'CXC', 'CXD', 'CXE', 'CXF', 'CXM', 'EXT', 'FPC', 'FPL', 'FSP', 'MNT', 'RTP', 'ITC', 'CX',
])

/**
 * Normaliza path de React Router para comparar con la matriz ACL.
 * Ej: /:factory/pda/sala-mezcla → /pda/sala-mezcla
 *     /CXC/lotes-trazabilidad → /lotes-trazabilidad
 */
export function normalizeRoutePathForAcl(pathname) {
  const path = (pathname || '/').split('?')[0] || '/'
  let stripped = path
    .replace(/:[^/]+/g, '')
    .replace(/\/+/g, '/')

  const parts = stripped.split('/').filter(Boolean)
  if (parts.length > 0 && FACTORY_PATH_CODES.has(parts[0].toUpperCase())) {
    parts.shift()
    stripped = '/' + parts.join('/')
  }

  const trimmed = stripped.replace(/\/$/, '')
  return trimmed || '/'
}

export const ACCESS_DENIED_PATH = '/acceso-denegado'

/**
 * Modo prueba: cualquier usuario autenticado ve todas las rutas; la matriz se ignora.
 * Poner en `false` cuando quieras aplicar default deny fila a fila en routeAccessMatrix.js.
 */
export const ROUTE_ACL_OPEN_FOR_TESTING = false

/**
 * Orden de “home” tras login: primera ruta permitida en esta lista.
 * @type {readonly string[]}
 */
export const HOME_PATH_CANDIDATES = [
  '/dashboard',
  '/ordenes-fabricacion',
  '/materiales-consumos',
  '/asistencia',
  '/empleados',
  '/planes-inspeccion',
  '/profile',
  '/events',
  '/datos-maestros-procesos',
  '/planes-ejecucion',
]

/** Prefijo de clave de grupo IdP en la matriz (ej. `@group:authentik Admins`). */
export const GROUP_KEY_PREFIX = '@group:'

/**
 * @param {object | null | undefined} userData
 * @returns {Set<string>}
 */
export function getUserRoleKeys(userData) {
  const { keys: keyList, unmapped } = buildRoleKeyDebugInfo(userData)
  if (ROUTE_ACL_DEBUG && unmapped.length > 0) {
    logAcl('warn', 'Asignaciones sin clave DEPT:Role (revisar departmentCodes.js)', unmapped)
  }
  return new Set(keyList)
}

/**
 * Claves DEPT:Role + `@group:NombreGrupo` para evaluar la matriz.
 * @param {object | null | undefined} userData
 * @returns {Set<string>}
 */
export function getUserAccessKeys(userData) {
  const keys = getUserRoleKeys(userData)
  const groups = userData?.user?.groups ?? []
  groups.forEach((g) => {
    if (typeof g === 'string' && g.trim()) {
      keys.add(`${GROUP_KEY_PREFIX}${g}`)
    }
  })
  return keys
}

/**
 * @deprecated Sin bypass global: el acceso lo define solo la matriz.
 */
export function hasGlobalBypass() {
  return false
}

/**
 * @param {object | null | undefined} userData
 */
export function hasAnyAssignedRole(userData) {
  const detail = userData?.organization?.roles_detail
  return Array.isArray(detail) && detail.length > 0
}

/**
 * @param {{ roles?: Record<string, boolean>, allowAny?: string[] }} rule
 * @returns {string[]}
 */
function ruleAllowKeys(rule) {
  if (Array.isArray(rule.allowAny) && rule.allowAny.length > 0) {
    return rule.allowAny
  }
  if (!rule.roles) return []
  return Object.keys(rule.roles).filter((k) => rule.roles[k])
}

/**
 * @param {string} pathname
 * @param {typeof ROUTE_ACCESS_MATRIX} matrix
 */
export function resolveRouteRule(pathname, matrix = ROUTE_ACCESS_MATRIX) {
  const path = normalizeRoutePathForAcl(pathname)

  const exact = matrix.find((r) => r.path === path)
  if (exact) return exact

  const prefixMatches = matrix
    .filter((r) => r.matchPrefix && path.startsWith(r.path.replace(/\/$/, '')))
    .sort((a, b) => b.path.length - a.path.length)

  return prefixMatches[0] ?? null
}

/**
 * Default deny: sin fila en matriz → no acceso (salvo rutas públicas).
 * @param {string} pathname
 * @param {object | null | undefined} userData
 * @param {typeof ROUTE_ACCESS_MATRIX} [matrix]
 */
/**
 * Explica por qué se permite o deniega una ruta (útil en consola).
 * @param {string} pathname
 * @param {object | null | undefined} userData
 */
export function explainRouteAccess(pathname, userData, matrix = ROUTE_ACCESS_MATRIX) {
  const rawPath = (pathname || '/').split('?')[0] || '/'
  const path = normalizeRoutePathForAcl(rawPath)
  const userKeys = [...getUserAccessKeys(userData)]
  const groups = userData?.user?.groups ?? []

  if (PUBLIC_PATHS.has(rawPath) || PUBLIC_PATHS.has(path)) {
    return { path: rawPath, allowed: true, reason: 'Ruta pública', userKeys, groups }
  }
  if (!userData) {
    return { path, allowed: false, reason: 'Sin datos de usuario', userKeys, groups }
  }
  if (ROUTE_ACL_OPEN_FOR_TESTING) {
    return { path, allowed: true, reason: 'ROUTE_ACL_OPEN_FOR_TESTING=true', userKeys, groups }
  }

  const rule = resolveRouteRule(path, matrix)
  if (!rule) {
    return { path, allowed: false, reason: 'Sin fila en matriz (default deny)', userKeys, groups }
  }

  const allowKeys = ruleAllowKeys(rule)
  if (allowKeys.includes('*')) {
    return { path, allowed: true, reason: 'Matriz: allow *', userKeys, groups, allowKeys }
  }
  if (allowKeys.includes('__AUTHENTICATED__')) {
    const allowed = hasAnyAssignedRole(userData) || groups.length > 0
    return {
      path,
      allowed,
      reason: allowed ? 'Matriz: __AUTHENTICATED__' : 'Sin roles ni grupos',
      userKeys,
      groups,
    }
  }

  const matched = allowKeys.filter((k) => userKeys.includes(k))
  let allowed = matched.length > 0

  if (allowed && isProdSupervisorTeamScoped(getUserRoleKeys(userData))) {
    if (!isProdSupervisorPathAllowed(path)) {
      return {
        path,
        allowed: false,
        reason: 'PROD:Supervisor limitado a People, Production, Process y utilidades',
        userKeys,
        groups,
        matchedKeys: matched,
      }
    }
  }

  if (allowed && isHrLaborOnlyScoped(getUserRoleKeys(userData))) {
    if (!isHrLaborPathAllowed(path)) {
      return {
        path,
        allowed: false,
        reason: 'RRHH limitado a People Management y utilidades',
        userKeys,
        groups,
        matchedKeys: matched,
      }
    }
  }

  return {
    path,
    allowed,
    reason: allowed
      ? `Matriz: coincide ${matched.join(', ')}`
      : `Matriz: ninguna clave del usuario en allow (${allowKeys.length} en fila)`,
    userKeys,
    groups,
    matchedKeys: matched,
    prodOperatorOnRoute: allowKeys.includes('PROD:Operator')
      ? rule.roles?.['PROD:Operator']
      : undefined,
  }
}

export function canAccessRoute(pathname, userData, matrix = ROUTE_ACCESS_MATRIX) {
  return explainRouteAccess(pathname, userData, matrix).allowed
}

/**
 * @param {object | null | undefined} userData
 * @param {readonly string[]} [candidates]
 */
export function getDefaultHomePath(userData, candidates = HOME_PATH_CANDIDATES) {
  for (const path of candidates) {
    if (canAccessRoute(path, userData)) return path
  }
  if (canAccessRoute('/profile', userData)) return '/profile'
  return ACCESS_DENIED_PATH
}

/** Recoge paths de hojas del menú lateral. */
function collectMenuPaths(items, acc = []) {
  if (!Array.isArray(items)) return acc
  for (const item of items) {
    if (item.submenu?.length) {
      collectMenuPaths(item.submenu, acc)
    } else if (item.path) {
      acc.push(item.path)
    }
  }
  return acc
}

export function filterSidebarMenuByAccess(menuItems, userData) {
  if (!Array.isArray(menuItems)) return menuItems

  const out = []
  for (const item of menuItems) {
    if (item.submenu?.length) {
      const sub = filterSidebarMenuByAccess(item.submenu, userData)
      if (sub.length > 0) {
        out.push({ ...item, submenu: sub })
      }
    } else if (item.path && canAccessRoute(item.path, userData)) {
      out.push(item)
    }
  }
  return out
}

/**
 * Resumen en consola al cargar permisos (dev / VITE_ROUTE_ACL_DEBUG).
 * @param {object | null | undefined} userData
 * @param {object[]} [fullMenu]
 */
export function logRouteAccessSession(userData, fullMenu = []) {
  if (!ROUTE_ACL_DEBUG || !userData) return

  const userKeys = [...getUserAccessKeys(userData)]
  const groups = userData?.user?.groups ?? []
  const rolesDetail = userData?.organization?.roles_detail ?? []

  const samplePaths = [
    '/dashboard',
    '/materiales-consumos',
    '/datos-maestros-procesos',
    '/events',
    '/ordenes-fabricacion',
    '/config-empresa',
  ]

  const samples = Object.fromEntries(
    samplePaths.map((p) => [p, explainRouteAccess(p, userData)]),
  )

  const menuPaths = collectMenuPaths(fullMenu)
  const allowedMenu = menuPaths.filter((p) => explainRouteAccess(p, userData).allowed)

  console.group(`${LOG_PREFIX} Sesión ACL`)
  console.log('ROUTE_ACL_OPEN_FOR_TESTING:', ROUTE_ACL_OPEN_FOR_TESTING)
  console.log('roles_detail:', rolesDetail)
  console.log('Grupos IdP:', groups)
  console.log('Claves ACL (DEPT:Role + @group):', userKeys)
  console.log('Muestra de rutas:', samples)
  console.log(`Menú sidebar: ${allowedMenu.length}/${menuPaths.length} rutas visibles`, allowedMenu)
  console.groupEnd()

  if (typeof window !== 'undefined') {
    window.__routeAcl = {
      explain: (path) => explainRouteAccess(path, userData),
      canAccess: (path) => canAccessRoute(path, userData),
      userKeys: () => [...getUserAccessKeys(userData)],
    }
    logAcl('log', 'Consola: window.__routeAcl.explain("/ruta"), .canAccess(), .userKeys()')
  }
}

/**
 * Log puntual al denegar o redirigir una ruta.
 * @param {string} pathname
 * @param {object} userData
 * @param {string} [redirectTo]
 */
export function logRouteAccessDenied(pathname, userData, redirectTo) {
  if (!ROUTE_ACL_DEBUG) return
  const explanation = explainRouteAccess(pathname, userData)
  logAcl('warn', `Acceso denegado a ${pathname}${redirectTo ? ` → redirect ${redirectTo}` : ''}`, explanation)
}

/**
 * @param {import('react').ReactElement[]} routes
 * @param {typeof ROUTE_ACCESS_MATRIX} matrix
 */
export function validateRouteAccessCoverage(routes, matrix = ROUTE_ACCESS_MATRIX) {
  const errors = []
  const matrixPaths = new Set(matrix.map((r) => r.path))

  routes.forEach((route) => {
    const rawPath = route?.props?.path
    if (!rawPath) return

    const basePath = normalizeRoutePathForAcl(rawPath)
    if (PUBLIC_PATHS.has(rawPath) || PUBLIC_PATHS.has(basePath)) return

    const hasExact = matrixPaths.has(rawPath) || matrixPaths.has(basePath)
    const hasPrefix = matrix.some(
      (r) => r.matchPrefix && (rawPath.startsWith(r.path) || basePath.startsWith(r.path)),
    )

    if (!hasExact && !hasPrefix) {
      errors.push(`Ruta sin entrada en matriz ACL: ${rawPath}`)
    }
  })

  return errors
}
